import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { message as antdMessage } from 'antd';
import chatStore from '../stores/chatStore';
import { useTheme } from '../theme/ThemeContext';
import ModelSelect from './ModelSelect';
import TemperatureControl from './TemperatureControl';
import ReasoningControl from './ReasoningControl';
import AttachmentBar from './AttachmentBar';
import AudioMicButton from './AudioMicButton';
import DropZone from '../files/DropZone';
import ImageGenForm from '../images/ImageGenForm';
import AudioGenForm from '../audio/AudioGenForm';
import VideoGenForm from '../videos/VideoGenForm';
import { autoSizePromptTextarea, getPromptMinRows, PROMPT_MOBILE_MAX_WIDTH } from '../utils/promptTextarea';

const MAX_CONCURRENT = 3;

const IconCopy = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M5 15V5a2 2 0 012-2h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const IconClear = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
    </svg>
);

const IconSend = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
            d="M5 12h12M13 6l6 6-6 6"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconStop = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
);

const Composer = observer(() => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const fileRef = useRef(null);
    const [dragActive, setDragActive] = useState(false);
    const taRef = useRef(null);

    const enqueueFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        const queue = [...files];
        const workers = Array.from({ length: Math.min(MAX_CONCURRENT, queue.length) }, async () => {
            while (queue.length) {
                const file = queue.shift();
                if (file) await chatStore.uploadAttachment(file);
            }
        });
        await Promise.all(workers);
    };

    const onPaste = (e) => {
        const items = Array.from(e.clipboardData?.items || []);
        const files = items
            .filter((i) => i.kind === 'file')
            .map((i) => i.getAsFile())
            .filter(Boolean);
        if (files.length) {
            e.preventDefault();
            enqueueFiles(files);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            chatStore.sendMessage();
        }
    };

    const autoSize = () => autoSizePromptTextarea(taRef.current);

    const setTaRef = (node) => {
        taRef.current = node;
        if (node) {
            autoSizePromptTextarea(node);
            requestAnimationFrame(() => autoSizePromptTextarea(node));
        }
    };

    useEffect(() => {
        autoSize();
        const id = requestAnimationFrame(() => autoSize());
        return () => cancelAnimationFrame(id);
    }, [chatStore.draft, chatStore.mode]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mq = window.matchMedia(`(max-width: ${PROMPT_MOBILE_MAX_WIDTH}px)`);
        const onChange = () => autoSize();
        if (mq.addEventListener) {
            mq.addEventListener('change', onChange);
            return () => mq.removeEventListener('change', onChange);
        }
        mq.addListener(onChange);
        return () => mq.removeListener(onChange);
    }, []);

    const draftText = chatStore.draft || '';
    const hasDraft = !!draftText.trim();
    const minRows = getPromptMinRows();

    const copyDraft = async () => {
        if (!hasDraft) return;
        try {
            await navigator.clipboard.writeText(draftText);
            antdMessage.success('Скопировано');
        } catch (e) {
            antdMessage.error(e.message || 'Не удалось скопировать');
        }
    };

    const clearDraft = () => {
        chatStore.setDraft('');
        requestAnimationFrame(autoSize);
    };

    const modelInfo = chatStore.selectedModelInfo;
    const supportsAudioInput = (modelInfo?.input_modalities || [])
        .map((m) => String(m).toLowerCase())
        .includes('audio');
    const hasImagePending = chatStore.attachments.some(
        (a) => (a.mime || '').startsWith('image/') || a.kind === 'image'
    );
    const hasAudioPending = chatStore.attachments.some(
        (a) => (a.mime || '').startsWith('audio/') || a.kind === 'audio'
    );
    const modalityWarning =
        hasImagePending && modelInfo && !(modelInfo.input_modalities || []).includes('image')
            ? 'Выбранная модель не поддерживает изображения. Выберите vision-модель.'
            : hasAudioPending && modelInfo && !supportsAudioInput
              ? 'Выбранная модель не поддерживает аудио. Выберите модель с audio input.'
              : null;

    const toolBtn = (title, onClick, disabled, children) => (
        <button
            type="button"
            title={title}
            disabled={disabled}
            onClick={onClick}
            className={`flex h-9 w-9 items-center justify-center rounded-xl transition disabled:cursor-not-allowed disabled:opacity-30 ${
                isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'
            }`}
        >
            {children}
        </button>
    );

    return (
        <div className={`border-t px-3 py-3 sm:px-0 sm:py-3 ${isDark ? 'border-white/10' : 'border-black/10'}`}>
            <div className="mx-auto w-full sm:w-[70%]">
                <div className="mb-2.5 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="min-w-0 w-full sm:flex-1">
                        <ModelSelect kind={chatStore.mode === 'chat' ? 'chat' : chatStore.mode} />
                    </div>
                    {chatStore.mode === 'chat' && (
                        <div className="flex w-full flex-nowrap items-center gap-2 sm:ml-auto sm:w-auto sm:shrink-0">
                            <ReasoningControl />
                            <TemperatureControl />
                        </div>
                    )}
                </div>

                {chatStore.streamError && (
                    <div className="mb-2 rounded-xl bg-red-500/10 px-3 py-2 text-xs text-red-600 dark:text-red-300">
                        {chatStore.streamError.message || 'Ошибка'}
                    </div>
                )}

                {chatStore.mode === 'audio' ? (
                    <DropZone active={dragActive}>
                        <div
                            onDragEnter={(e) => {
                                e.preventDefault();
                                setDragActive(true);
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            onDragLeave={() => setDragActive(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragActive(false);
                                const files = Array.from(e.dataTransfer.files || []).filter((f) =>
                                    (f.type || '').startsWith('audio/')
                                );
                                if (!files.length) return;
                                enqueueFiles(files);
                            }}
                        >
                            <AttachmentBar />
                            <AudioGenForm
                                prompt={chatStore.audioDraft}
                                onPromptChange={(v) => chatStore.setAudioDraft(v)}
                            />
                        </div>
                    </DropZone>
                ) : chatStore.mode === 'video' ? (
                    <DropZone active={dragActive}>
                        <div
                            onDragEnter={(e) => {
                                e.preventDefault();
                                setDragActive(true);
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            onDragLeave={() => setDragActive(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragActive(false);
                                const images = Array.from(e.dataTransfer.files || []).filter((f) =>
                                    (f.type || '').startsWith('image/')
                                );
                                if (!images.length) return;
                                enqueueFiles(images);
                            }}
                        >
                            <AttachmentBar />
                            <VideoGenForm
                                prompt={chatStore.videoDraft}
                                onPromptChange={(v) => chatStore.setVideoDraft(v)}
                            />
                        </div>
                    </DropZone>
                ) : chatStore.mode === 'image' ? (
                    <DropZone active={dragActive}>
                        <div
                            onDragEnter={(e) => {
                                e.preventDefault();
                                setDragActive(true);
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            onDragLeave={() => setDragActive(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragActive(false);
                                const images = Array.from(e.dataTransfer.files || []).filter((f) =>
                                    (f.type || '').startsWith('image/')
                                );
                                if (!images.length) return;
                                enqueueFiles(images);
                            }}
                        >
                            <AttachmentBar />
                            <ImageGenForm
                                prompt={chatStore.imageDraft}
                                onPromptChange={(v) => chatStore.setImageDraft(v)}
                            />
                        </div>
                    </DropZone>
                ) : (
                    <DropZone active={dragActive}>
                        <AttachmentBar />
                        {modalityWarning && (
                            <div className="mb-2 rounded-xl bg-amber-500/15 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                                {modalityWarning}
                            </div>
                        )}
                        <div
                            className={`overflow-hidden rounded-2xl border shadow-sm ${
                                isDark ? 'border-white/10 bg-[#2f2f2f]' : 'border-black/10 bg-white'
                            }`}
                            onDragEnter={(e) => {
                                e.preventDefault();
                                setDragActive(true);
                            }}
                            onDragOver={(e) => e.preventDefault()}
                            onDragLeave={() => setDragActive(false)}
                            onDrop={(e) => {
                                e.preventDefault();
                                setDragActive(false);
                                enqueueFiles(e.dataTransfer.files);
                            }}
                        >
                            <div className="flex items-end gap-1.5 px-2 py-2 sm:gap-2 sm:px-3 sm:py-2.5">
                                <textarea
                                    ref={setTaRef}
                                    value={chatStore.draft}
                                    onChange={(e) => {
                                        chatStore.setDraft(e.target.value);
                                        autoSizePromptTextarea(e.target);
                                    }}
                                    onKeyDown={onKeyDown}
                                    onPaste={onPaste}
                                    rows={minRows}
                                    placeholder="Сообщение…"
                                    className={`bananas-msg-font box-border min-w-0 flex-1 resize-none overflow-y-hidden bg-transparent px-2 py-2 leading-normal outline-none placeholder:opacity-45 sm:px-2 sm:py-2.5 ${
                                        isDark ? 'text-gray-100' : 'text-gray-900'
                                    }`}
                                />
                                <div className="bananas-btn-scale mb-0.5 shrink-0">
                                    {chatStore.streaming ? (
                                        <button
                                            type="button"
                                            onClick={() => chatStore.stop()}
                                            title="Остановить"
                                            aria-label="Остановить"
                                            className="flex h-9 w-9 items-center justify-center rounded-full bg-red-600 text-white transition hover:bg-red-500"
                                        >
                                            <IconStop />
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            disabled={
                                                (!chatStore.draft.trim() &&
                                                    !chatStore.attachments.some(
                                                        (a) => a.status === 'ready'
                                                    )) ||
                                                !!modalityWarning
                                            }
                                            onClick={() => chatStore.sendMessage()}
                                            title="Отправить"
                                            aria-label="Отправить"
                                            className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-35"
                                        >
                                            <IconSend />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div
                                className={`bananas-btn-scale flex items-center gap-1 border-t px-3 py-2 ${
                                    isDark ? 'border-white/10' : 'border-black/8'
                                }`}
                            >
                                <input
                                    ref={fileRef}
                                    type="file"
                                    multiple
                                    className="hidden"
                                    accept={
                                        supportsAudioInput
                                            ? 'image/png,image/jpeg,image/webp,image/gif,application/pdf,audio/*,.wav,.mp3,.m4a,.ogg,.webm,.flac,.txt,.md,.csv,.json,.docx,.py,.js,.ts,.tsx,.jsx,.html,.css,.yaml,.yml'
                                            : 'image/png,image/jpeg,image/webp,image/gif,application/pdf,.txt,.md,.csv,.json,.docx,.py,.js,.ts,.tsx,.jsx,.html,.css,.yaml,.yml'
                                    }
                                    onChange={(e) => {
                                        enqueueFiles(e.target.files);
                                        e.target.value = '';
                                    }}
                                />
                                {toolBtn('Прикрепить файл', () => fileRef.current?.click(), false, '＋')}
                                {supportsAudioInput ? <AudioMicButton /> : null}
                                {toolBtn('Скопировать', copyDraft, !hasDraft, <IconCopy />)}
                                {toolBtn('Очистить', clearDraft, !hasDraft, <IconClear />)}
                            </div>
                        </div>
                    </DropZone>
                )}
            </div>
        </div>
    );
});

export default Composer;
